import * as XLSX from 'xlsx'

import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import moment, { Moment } from 'moment-timezone';

const writeXlsxFile = async (data: any[][], filename: string = 'reporting.xlsx') => {
  console.log("Data to be exported:", JSON.stringify(data));
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet('Sheet1');
  worksheet.addRows(data);

  // Headers that need to be formatted as currency
  const currencyHeaders = ['TaxAmount', 'GrossPremium', 'TaxAmt', 'NonAdmittedPremium', 'NonTaxableFees', 'TaxableFees', 'TaxBalance', 'taxAmount', 'taxReported'];
  const dateHeaders = ['FilingDueDate', 'InvoiceDate', 'PolicyEffectiveDate', 'PolicyExpDate', 'TransactionEffDate', 'InvDate', 'PolicyEffDate', 'PolicyExpDate', 'DateInvoiceAdded',
    'DateFiled', 'PaymentNoticeDate', 'DiligentSearchProcessDate', 'DiligentSearchToGMAC', 'FirstRequest', 'SecondRequest', 'ThirdRequest', 'Suspended', 'DateComplete'];

    if( filename == "ReconciliationQueue" || filename == "AgedPayablesReport" || filename == "QualityControlReport"){
     
      filename = 'reporting.xlsx';
      const totalsHeaders = ['GrossPremium', 'TaxAmt', 'TaxableFees'];
      const totalsRow = Array(data[0].length).fill('');
      totalsHeaders.forEach((header) => {
        const headerIndex = data[0].indexOf(header);
        if (headerIndex !== -1) {
          const total = data.slice(1).reduce((sum, row) => {
            const cleanedValue = typeof row[headerIndex] === 'string' 
              ? row[headerIndex].replace(/[,\$]/g, '').trim()
              : row[headerIndex];
        
            const numericValue = parseFloat(cleanedValue) || 0;
        
            return sum + numericValue;
          }, 0);
          totalsRow[headerIndex] = `$${total.toFixed(2)}`;
          console.log('TaxAmt Total:', totalsRow[headerIndex]); 
        }
      });
      
      worksheet.addRow(totalsRow); // Append totals row to worksheet
    
      // Apply styling to Totals Row
      const totalsRowNumber = worksheet.rowCount; // Get the row number of the totals row
      const totalsStyle = {
        font: { bold: true },
        alignment: { horizontal: 'right' as ExcelJS.Alignment['horizontal'] },
      };
  
      worksheet.getRow(totalsRowNumber).eachCell({ includeEmpty: true }, (cell, colNumber) => {
        const headerName = data[0][colNumber - 1].trim();
        if (totalsHeaders.includes(headerName)) { // Only style totals headers
          cell.font = totalsStyle.font;
          cell.alignment = { horizontal: 'right' };
          cell.numFmt = '"$"#,##0.00;\-"$"#,##0.00'; // Currency format
  
          if (headerName === 'TaxAmt') {
            cell.fill = {
              type: 'pattern',
              pattern: 'solid',
              fgColor: { argb: 'FFFFFF00' }, // Yellow fill color
            };
          }
        }
      });
    }

  // Apply styling to header row
  const headerRowNumber = filename === "YearToDateReport" ? 4 : 1;
  const headerRowIndex = filename === "YearToDateReport" ? 3 : 0; // Index in data array
  
  const headerStyle = {
    fill: {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FF106C8C' }, // Header background color
    },
    font: {
      bold: true,
      color: { argb: 'FFFFFFFF' }, // White text color
    },
    alignment: {
      vertical: 'middle',
      horizontal: 'left', // Left align header text
    },
  };

  const headerRow = worksheet.getRow(headerRowNumber);
  headerRow.eachCell({ includeEmpty: true }, (cell) => {
    Object.assign(cell, headerStyle);
  });
  const conditionalFormatStartRow = filename === "YearToDateReport" ? 6 : 2;
  const conditionalFormatEndRow = worksheet.rowCount;
  
  worksheet.addConditionalFormatting({
    ref: `A${conditionalFormatStartRow}:${worksheet.getColumn(data[headerRowIndex].length).letter}${conditionalFormatEndRow}`, // Adjust based on your data
    rules: [
      {
        type: 'expression',
        formulae: filename === "YearToDateReport" 
          ? ['MOD(ROW()-5,2)=0'] // Adjust for starting at row 6
          : ['MOD(ROW(),2)=0'], // Use 'formulae' array for the conditional formatting rule
        style: {
          fill: {
            type: 'pattern',
            pattern: 'solid',
            bgColor: { argb: 'FFADD8E6' }, // Light blue color for even rows
          },
        },
        priority: 1, // Added 'priority' to fix the TypeScript error
      },
    ],
  });

  // Apply alternating row colors, alignment, and format currency
  const columnCount = data[headerRowIndex].length; 
  const dataStartRow = filename === "YearToDateReport" ? 6 : 2;
  for (let i = dataStartRow; i <= worksheet.rowCount; i++) {
    const row = worksheet.getRow(i);

    // Apply formatting only up to the number of header columns
    for (let j = 1; j <= columnCount; j++) {
      const cell = row.getCell(j);

      const headerName = data[headerRowIndex][j - 1] as string; // Get the header name based on the column index

      if (currencyHeaders.includes(headerName)) {
        // Ensure cell.value is a string, and handle null/undefined cases
        const cellValue = cell.value ? cell.value.toString() : '0';

        // Check for negative values in parentheses format, e.g., ($697.00)
        let numericValue: number;
        if (cellValue.includes('(') && cellValue.includes(')')) {
          // It's a negative value in parentheses, remove parentheses and set the value as negative
          const cleanValue = cellValue.replace(/[($)]/g, ''); // Remove '(' and ')'
          numericValue = parseFloat(cleanValue.replace(/[$,]/g, '')) * -1; // Parse as negative
        } else {
          // Positive or regular value
          numericValue = parseFloat(cellValue.replace(/[$,]/g, '') || '0'); // Remove '$' and ',' and parse as float
        }

        // Assign the numeric value back to the cell
        cell.value = numericValue;

        // Apply currency formatting
        cell.numFmt = '"$"#,##0.00;\-"$"#,##0.00';
      }

      if (dateHeaders.includes(headerName)) {
        // Apply date formatting
        cell.numFmt = 'mm/dd/yyyy';
      }

      cell.border = {
        top: { style: 'thin', color: { argb: 'FFADD8E6' } }, // Blue border
        left: { style: 'thin', color: { argb: 'FFADD8E6' } },
        bottom: { style: 'thin', color: { argb: 'FFADD8E6' } },
        right: { style: 'thin', color: { argb: 'FFADD8E6' } },
      };

      // Client Inbox export: wrap + full-cell fill for Error Types when populated
      if (filename.includes('ClientInbox') && headerName === 'Error Types') {
        cell.alignment = {
          wrapText: true,
          vertical: 'top',
          horizontal: 'left',
        };
        const raw = cell.value;
        const text = raw == null ? '' : String(raw).trim();
        if (text.length > 0) {
          cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFE8F4FC' },
          };
        }
      }
    }
  }

  // Apply autofilter to each column
  // For YearToDateReport: headers are in row 3, for others in row 1
  worksheet.autoFilter = { from: { row: headerRowNumber, column: 1 }, to: { row: headerRowNumber, column: columnCount } };

  // Set column widths
  if (filename === "YearToDateReport") {
    // Custom column widths for YearToDateReport
    const columnWidths: { [key: string]: number } = {
      'Client': 40,
      'State': 20,
      'Invoice Number': 18,
      'Insured Name': 35,
      'Policy Number': 25,
      'Date Filed': 15,
      'Transaction Type': 25,
      'Report Name': 50
    };
    
    // Set widths based on header names
    const headersForWidth = data[headerRowIndex];
    for (let i = 0; i < columnCount; i++) {
      const headerName = headersForWidth[i] as string;
      const width = columnWidths[headerName] || 20;
      worksheet.getColumn(i + 1).width = width;
    }
  } else if (filename.startsWith("StampedDocuments_")) {
    // Custom column widths for StampedDocuments
    const columnWidths: { [key: string]: number } = {
      'Type': 40,
      'Client': 50,
      'Sent On': 15,
      'Sent By': 20
    };
    
    // Set widths based on header names
    const headersForWidth = data[headerRowIndex];
    for (let i = 0; i < columnCount; i++) {
      const headerName = headersForWidth[i] as string;
      const width = columnWidths[headerName] || 20;
      worksheet.getColumn(i + 1).width = width;
    }
  } else if (filename.includes('ClientInbox')) {
    const headersForWidth = data[headerRowIndex];
    for (let i = 0; i < columnCount; i++) {
      const headerName = headersForWidth[i] as string;
      const width = headerName === 'Error Types' ? 48 : headerName === 'Message' ? 36 : 20;
      worksheet.getColumn(i + 1).width = width;
    }
  } else {
    // Default width for all other exports
    worksheet.columns = Array(columnCount).fill({ width: 20 });
  }

  // Format title row and summary rows for YearToDateReport
  if (filename === "YearToDateReport") {
    // Format title row (row 1) - "Year to Date Report"
    const titleRow = worksheet.getRow(1);
    const titleCell = titleRow.getCell(1);
    const titleValue = titleCell.value?.toString() || '';
    if (titleValue.trim() === 'Year to Date Report') {
      titleCell.font = { bold: true, size: 22 };
      // Remove all borders from title row
      titleRow.eachCell({ includeEmpty: true }, (cell) => {
        cell.border = {};
      });
    }
    
    // Remove borders from empty row (row 3)
    const emptyRow = worksheet.getRow(3);
    emptyRow.eachCell({ includeEmpty: true }, (cell) => {
      cell.border = {};
    });
  }
  
  // Format summary rows (e.g., "Transaction Total") as bold
  for (let i = 1; i <= worksheet.rowCount; i++) {
    const row = worksheet.getRow(i);
    const firstCellValue = row.getCell(1).value?.toString() || '';
    if (firstCellValue.trim() === 'Transaction Total') {
      if (filename === "YearToDateReport") {
        // Special formatting for YearToDateReport to match image
        // Summary row is in row 2
        const firstCell = row.getCell(1);
        const secondCell = row.getCell(2);
        
        // Style the "Transaction Total" label - bold, size 24
        firstCell.font = { bold: true, size: 24 };
        // Remove borders from first cell
        firstCell.border = {};
        
        // Style the count cell - bold, size 24, no border
        if (secondCell && secondCell.value) {
          secondCell.font = { bold: true, size: 24 };
          // Remove border from count cell
          secondCell.border = {};
        }
        
        // Remove borders from remaining cells in the summary row
        for (let j = 3; j <= columnCount; j++) {
          const cell = row.getCell(j);
          cell.border = {};
        }
      } else {
        // Default formatting for other reports
        row.eachCell({ includeEmpty: true }, (cell) => {
          cell.font = { bold: true, size: 24 };
        });
      }
    }
  }

  // Freeze header row
  // For YearToDateReport: freeze at row 4 (headers), for others at row 1
  worksheet.views = [{ state: 'frozen', xSplit: 0, ySplit: headerRowNumber }];

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

  if(!window.location.href.includes('Customer')){
    saveAs(blob, filename);
  }
  const file = new File([blob], filename, { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

  return file
  console.timeEnd('===writeXlsxFile===')
};

// const writeXlsxFile = async (data: any[][], filename: string = 'reporting.xlsx') => {

//   const workbook = new ExcelJS.Workbook();
//   const worksheet = workbook.addWorksheet('Sheet1');
//   worksheet.addRows(data);

//   // Apply styling to header row
//   const headerRow = worksheet.getRow(1);
//   headerRow.eachCell({ includeEmpty: true }, (cell) => {
//     cell.fill = {
//       type: 'pattern',
//       pattern: 'solid',
//       fgColor: { argb: 'FF106C8C' } // Header background color
//     };
//     cell.font = {
//       bold: true,
//       color: { argb: 'FFFFFFFF' } // White text color
//     };
//     cell.alignment = {
//       vertical: 'middle',
//       horizontal: 'left' // Left align header text
//     };
//   });
//   // Apply alternating row colors and alignment
//   for (let i = 2; i <= worksheet.rowCount; i++) {
//     const row = worksheet.getRow(i);
//     const color = i % 2 === 0 ? 'FFADD8E6' : ''; // Light blue color for even rows
//     row.fill = {
//       type: 'pattern',
//       pattern: 'solid',
//       fgColor: { argb: color }

//     }
//     row.eachCell({ includeEmpty: true }, (cell) => {

//       cell.border = {
//         top: { style: 'thin', color: { argb: 'FFADD8E6' } }, // Blue border
//         left: { style: 'thin', color: { argb: 'FFADD8E6' } },
//         bottom: { style: 'thin', color: { argb: 'FFADD8E6' } },
//         right: { style: 'thin', color: { argb: 'FFADD8E6' } }
//       };
//     });
//   }

//   // Apply autofilter to each column
//   const columnCount = data[0].length;
//   worksheet.autoFilter = { from: { row: 1, column: 1 }, to: { row: 1, column: columnCount } };


//   for (let i = 1; i <= columnCount; i++) {
//     const column = worksheet.getColumn(i);  
//     column.width = 20;  
//   }
//   worksheet.views = [{ state: 'frozen', xSplit: 0, ySplit: 1 }];

//   const buffer = await workbook.xlsx.writeBuffer();
//   const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

//   saveAs(blob, filename); 
// };

// const writeXlsxFile = async (data: any, filename: string = 'reporting.xlsx') => {
//   const workbook = XLSX.utils.book_new()
//   const worksheet = XLSX.utils.aoa_to_sheet(data)
//   const headerRange = XLSX.utils.decode_range(worksheet['!ref']!);
//   worksheet['!autofilter'] = { ref: XLSX.utils.encode_range({
//     s: { r: 0, c: 0 },
//     e: { r: 0, c: headerRange.e.c }
//   })};
//   XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1')
//   await XLSX.writeFile(workbook, filename)
// }

const readXlsxFile = async (file: File, headerNames?: string[]) => {
  try {
    const data = await parseXlsxFile(file, headerNames)
    console.log('success reading xlsx file')
    return data as any
  } catch (error) {
    console.error('failed to read xlsx file')
  }
}
const parseXlsxFile = async (file: File, headerNames?: string[]) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = (_e) => {
      try {
        const buffArr = _e.target?.result as ArrayBuffer
        const workbook = XLSX.read(buffArr, { type: 'binary' })

        const sheetsData: any = {}
        workbook.SheetNames.forEach(sheetName => {
          // const sheetName = workbook.SheetNames[0]
          const sheet = workbook.Sheets[sheetName]
          let headerRow = 0;
          for (let i = 1; i <= 20; i++) {
            const cell = sheet[XLSX.utils.encode_cell({ r: i - 1, c: 0 })]

            if (cell && typeof cell.v === 'string') {
              headerRow = i - 1;
              break;
            }
          }
          const jsonData: any[] = XLSX.utils.sheet_to_json(sheet, { header: 1, range: headerRow, defval: '' })
          // Set the first array item as Row number that matches the Excel file's row number.
          const jsonDataRows = jsonData.map((item, index) => ([headerRow + index + 1, ...item]))

          // Find header row index
          const lenList = jsonDataRows.map((d) => (d as any[]).length)
          const headerIndex = indexOfHeader(lenList)
          if (headerIndex > 0) {
            jsonDataRows.splice(0, headerIndex)
          }
          sheetsData[sheetName] = jsonDataRows
        })
        resolve(sheetsData)
      } catch (_error) {
        reject(_error)
      }
    }
    reader.onerror = (_error) => {
      reject(_error)
    }
    const fileExtension = file.name.split('.').pop()
    if (fileExtension === 'csv') {
      reader.readAsText(file)
    } else {
      reader.readAsArrayBuffer(file)
    }
  })
}

const convertDateFromXlsx = (val: number) => {
  const excelBaseDate = new Date(1899, 11, 30); // Excel's base date for date serial
  const ms = 24 * 60 * 60 * 1000; // Number of milliseconds in a day
  const offset = val * ms; // Calculate the offset in milliseconds
  const parsedDate = new Date(excelBaseDate.getTime() + offset); // Add the offset to the base date
  return parsedDate; // Return the parsed date
};

const isValidOtrImportYear = (date: Date): boolean => {
  if (Number.isNaN(date.valueOf())) {
    return false;
  }
  const year = date.getFullYear();
  return year >= 1990 && year <= 2990;
};

const toOtrImportDateString = (date: Date): string => {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}T00:00:00`;
};

/**
 * Normalize an OTR Excel date cell to local `YYYY-MM-DDT00:00:00` for SQL DATE conversion.
 * Returns undefined for blank/invalid values so imports never emit `NaN-NaN-NaN...` strings
 * that cause SQL error 241 and fail the entire batch.
 *
 * Supports: Excel serial numbers, numeric serial strings, Date objects, and common date strings.
 */
const formatOtrImportDate = (raw: unknown): string | undefined => {
  if (raw === null || raw === undefined || raw === '') {
    return undefined;
  }
  if (typeof raw === 'string' && raw.trim() === '') {
    return undefined;
  }

  let date: Date | undefined;

  // 1) Excel serial day number (most common from SheetJS)
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    date = convertDateFromXlsx(raw);
  } else if (raw instanceof Date) {
    // 2) Already a Date (e.g. cellDates: true)
    date = raw;
  } else if (typeof raw === 'string') {
    const trimmed = raw.trim();

    // 3) Numeric string → treat as Excel serial when JS Date cannot parse it
    //    (new Date("46113") is Invalid Date; old code dropped these as undefined)
    if (/^\d+(\.\d+)?$/.test(trimmed)) {
      const serial = Number(trimmed);
      if (Number.isFinite(serial)) {
        const fromSerial = convertDateFromXlsx(serial);
        if (isValidOtrImportYear(fromSerial)) {
          date = fromSerial;
        }
      }
    }

    // 4) Common display / ISO strings
    if (!date) {
      const parsed = new Date(trimmed);
      if (isValidOtrImportYear(parsed)) {
        date = parsed;
      }
    }

    // 5) Moment fallback for MM/DD/YYYY and similar (locale-safe)
    if (!date) {
      const m = moment(trimmed, ['M/D/YYYY', 'MM/DD/YYYY', 'M-D-YYYY', 'MM-DD-YYYY', 'YYYY-MM-DD', moment.ISO_8601], true);
      if (m.isValid()) {
        const fromMoment = m.toDate();
        if (isValidOtrImportYear(fromMoment)) {
          date = fromMoment;
        }
      }
    }
  } else {
    const parsed = new Date(raw as any);
    if (isValidOtrImportYear(parsed)) {
      date = parsed;
    }
  }

  if (!date || !isValidOtrImportYear(date)) {
    return undefined;
  }

  return toOtrImportDateString(date);
};

const isOtrImportDateCellBlank = (raw: unknown): boolean => {
  if (raw === null || raw === undefined || raw === '') {
    return true;
  }
  if (typeof raw === 'string' && raw.trim() === '') {
    return true;
  }
  return false;
};

/** True when the cell has a value but cannot be normalized for OTR import (e.g. 5/11/227). */
const isOtrImportDateCellInvalid = (raw: unknown): boolean => {
  if (isOtrImportDateCellBlank(raw)) {
    return false;
  }
  return formatOtrImportDate(raw) === undefined;
};

type OtrImportColumnLookup = (header: string) => { field: string; inputType: string } | undefined;

/** Returns OTR date field names that are present in the row but failed validation. */
const collectInvalidOtrImportDateFields = (
  xlsHeaders: string[],
  item: unknown[],
  getColumn: OtrImportColumnLookup
): string[] => {
  const invalidFields: string[] = [];
  xlsHeaders.forEach((header, idx) => {
    const column = getColumn(header);
    if (!column || column.inputType !== 'date') {
      return;
    }
    if (isOtrImportDateCellInvalid(item[idx])) {
      invalidFields.push(column.field);
    }
  });
  return invalidFields;
};

const formatInvalidOtrImportDateMessage = (fields: string[]): string => {
  return `Invalid date(s): ${fields.join(', ')}`;
};

const isOtrImportExcelCellBlank = (val: unknown): boolean => {
  if (val === null || val === undefined || val === '') {
    return true;
  }
  if (typeof val === 'string' && val.trim() === '') {
    return true;
  }
  return false;
};

/**
 * True when the Excel row has at least one non-blank cell besides _rowNum.
 * Skips trailing used-range rows that are visually empty (defval '' fills every column).
 */
const hasOtrImportExcelRowData = (
  xlsHeaders: string[],
  item: unknown[]
): boolean => {
  for (let idx = 0; idx < xlsHeaders.length; idx++) {
    if (xlsHeaders[idx] === '_rowNum') {
      continue;
    }
    if (!isOtrImportExcelCellBlank(item[idx])) {
      return true;
    }
  }
  return false;
};

const convertDateToXlsx = (val: Date) => {
  const excelBaseDate = new Date(1899, 11, 30)
  const ms = 24 * 60 * 60 * 1000
  const offset = val.getTime() - excelBaseDate.getTime()
  const parsedDate = Math.floor(offset / ms)
  return parsedDate
}

const compactString = (val: string | undefined) => {
  return val?.toLowerCase().replaceAll(' ', '')
}

const isNanOrZero = (val: string | undefined) => {
  return isNaN(+(val ?? '')) || (+(val ?? 0) === 0)
}
const isEmptyOrUndefined = (str: string | undefined) => {
  return str === "" || str === null || str === undefined
}
// const isInvoiceNumber = (str: string | undefined) => {
//   if (!str) return false
//   return /\d/.test(str)
// }

const isInvoiceNumber = (str: string | undefined) => {
  if (!str) return false;
  return /^[A-Za-z0-9]+$/.test(str);
}

const indexOfHeader = (arr: number[]) => {
  if (arr.length === 0) {
    return 0
  }
  let max = arr[0]
  let maxIndex = 0
  for (let i = 1; i < arr.length; i++) {
    if (arr[i] > max + 2) {
      maxIndex = i
      max = arr[i]
    }
  }
  return maxIndex
}

const toCurrency = (val: any) => {
  return (val === "" || val === undefined) ? undefined : (+(+val).toFixed(2).replace(/\.?0+$/, '')).toLocaleString()
}

const isUserRoleInclude = (userRoles: string[] | undefined, arr: string[] | undefined) => {
  if (!userRoles || !Array.isArray(arr)) return false
  const roles = userRoles.map(item => compactString(item))
  const arrCompact = arr.map(item => compactString(item))
  return roles.some((role) => arrCompact.includes(role))
}

const isMiscUser = (userRoles: (string | undefined)[] | string | undefined) => {
  if (!userRoles) return false
  const roles = Array.isArray(userRoles) ? userRoles : [userRoles]
  return roles.some(role => role && ['super admin', 'misc filer', 'misc state reporter'].includes(role?.toLowerCase()))
}

const isStaffUser = (userRoles: (string | undefined)[] | string | undefined) => {
  if (!userRoles) return false
  const roles = Array.isArray(userRoles) ? userRoles : [userRoles]
  return roles.some(role => role && ['super admin', 'misc filer', 'misc state reporter'].includes(role?.toLowerCase()))
}

const getErrorMessage = (err: any) => {
  const result = Object.entries(err).map(([key, val]) => `${key}: ${val}`).join(", ")
  return result
}

const safeTrim = <T>(
  value: T,
  defaultValue?: T
): T extends string ? string : T => {
  if (typeof value === 'string') {
    return value.trim() as T extends string ? string : T
  }
  if (defaultValue !== undefined) {
    return defaultValue as T extends string ? string : T
  }
  return value as T extends string ? string : T
}

const KNOWN_FORMATS = [
  'MM-DD-YYYY',
  'MM-DD-YY',
]
const safeMoment = (val: string | number | Date | Moment): Moment => {
  if (typeof val === 'string') {
    const parsed = moment(val, KNOWN_FORMATS, true)
    if (parsed.isValid()) {
      return parsed
    }
    // fallback to native constructor with warning if still invalid
    return moment(val) // may warn, but at this point it's truly unknown
  }

  return moment(val) // safe for Date, number, Moment, etc.
}

/** IANA time zone for UI: all dates are displayed in Mountain Time (MST/MDT). */
export const UI_DISPLAY_TIMEZONE = 'America/Denver';

/**
 * Parses a date string from API/backend into a moment in UI timezone.
 * Supports: ISO (YYYY-MM-DD, YYYY-MM-DDTHH:mm:ss), MM-DD-YYYY, MM/DD/YYYY,
 * MM-DD-YY, MM/DD/YY, and C#-style "Mon DD YYYY HH:MMAM/PM" (e.g. "Oct 16 2023 12:00AM").
 * @param calendarDateOnly - If true, the returned moment is the same calendar day (no TZ shift).
 *   Use for Effective, Due, Expiry, Invoice dates so the displayed day is not shifted to MST.
 */
const parseAnyDateString = (value: string | number | Date | null | undefined, calendarDateOnly?: boolean): Moment | null => {
  if (value == null || value === '') return null
  const str = String(value).trim()
  if (!str) return null

  const datePart = str.match(/^(\d{4}-\d{2}-\d{2})/)?.[1]
  // Only treat as date-only when there is no time or time is midnight UTC (so we don't shift the calendar day)
  const isDateOnlyOrMidnightUtc =
    datePart != null &&
    (/^\d{4}-\d{2}-\d{2}$/.test(str) ||
      /^\d{4}-\d{2}-\d{2}T00:00:00(\.\d+)?Z?$/i.test(str))
  if (isDateOnlyOrMidnightUtc) {
    const m = moment.tz(datePart, 'YYYY-MM-DD', UI_DISPLAY_TIMEZONE)
    return m.isValid() ? m : null
  }

  // ISO with non-midnight time: parse full value and convert to display timezone so time is shown
  if (datePart != null && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?(\.\d+)?Z?$/i.test(str)) {
    const m = moment.utc(str)
    if (m.isValid()) return m.tz(UI_DISPLAY_TIMEZONE)
  }

  const isDisplayDateOnly4 = /^\d{1,2}-\d{1,2}-\d{4}$/.test(str) || /^\d{1,2}\/\d{1,2}\/\d{4}$/.test(str)
  if (isDisplayDateOnly4) {
    const m = moment.tz(str, str.includes('/') ? 'MM/DD/YYYY' : 'MM-DD-YYYY', UI_DISPLAY_TIMEZONE)
    return m.isValid() ? m : null
  }

  const isDisplayDateOnly2 = /^\d{1,2}-\d{1,2}-\d{2}$/.test(str) || /^\d{1,2}\/\d{1,2}\/\d{2}$/.test(str)
  if (isDisplayDateOnly2) {
    const m = moment.tz(str, str.includes('/') ? 'MM/DD/YY' : 'MM-DD-YY', UI_DISPLAY_TIMEZONE)
    return m.isValid() ? m : null
  }

  // C#-style "Oct 16 2023 12:00AM" / "Aug 13 2023 12:00AM" (optional space before AM/PM)
  const cSharpStyle = /^[A-Za-z]{3}\s+\d{1,2}\s+\d{4}\s+\d{1,2}:\d{2}\s*[AP]M$/i.test(str)
  if (cSharpStyle) {
    const m = moment.utc(str, ['MMM D YYYY h:mmA', 'MMM DD YYYY h:mmA', 'MMM D YYYY hh:mmA', 'MMM DD YYYY hh:mmA'], true)
    if (m.isValid()) {
      if (calendarDateOnly) return moment.tz({ year: m.year(), month: m.month(), date: m.date() }, UI_DISPLAY_TIMEZONE)
      return m.tz(UI_DISPLAY_TIMEZONE)
    }
  }

  const m = moment.utc(value)
  if (!m.isValid()) return null
  if (calendarDateOnly) return moment.tz({ year: m.year(), month: m.month(), date: m.date() }, UI_DISPLAY_TIMEZONE)
  return m.tz(UI_DISPLAY_TIMEZONE)
}

/**
 * Parses any supported date string and returns an ISO string for form/API use (e.g. DatePicker value).
 * Returns null if the value is empty or unparseable.
 * @param calendarDateOnly - If true, use the calendar day only (no TZ shift). Use for Effective, Due, Expiry, Invoice dates.
 */
export const parseAnyDateToIso = (value: string | number | Date | null | undefined, calendarDateOnly?: boolean): string | null => {
  const m = parseAnyDateString(value, calendarDateOnly)
  return m != null ? m.utc().toISOString() : null
}

/**
 * Parses any supported date string and returns a date-only string YYYY-MM-DD (no time, no TZ).
 * Use for Effective, Due, Expiry, Invoice dates in forms so the DatePicker shows the same calendar day in all timezones.
 * Returns null if the value is empty or unparseable.
 */
export const parseAnyDateToDateOnly = (value: string | number | Date | null | undefined): string | null => {
  const m = parseAnyDateString(value, true)
  return m != null ? m.format('YYYY-MM-DD') : null
}

/**
 * Formats a UTC value for display in the UI. All UI dates are shown in MST (America/Denver).
 * DB stores UTC; UI displays MST.
 * Supports all common API date string formats: ISO, MM-DD-YYYY, MM/DD/YYYY, MM-DD-YY, MM/DD/YY,
 * and C#-style "Mon DD YYYY HH:MMAM/PM" (e.g. "Oct 16 2023 12:00AM").
 * @param calendarDateOnly - If true, display the calendar day only (no TZ shift). Use for Effective, Due, Expiry, Invoice dates.
 */
const formatUtcToLocal = (value: string | number | Date | null | undefined, formatStr: string, calendarDateOnly?: boolean): string => {
  if (value == null || value === '') return 'N/A'
  const m = parseAnyDateString(value, calendarDateOnly)
  return m != null ? m.format(formatStr) : 'N/A'
}

/**
 * Returns current UTC date/time as ISO string for sending to API (DB stores UTC).
 */
export const toUtcIsoString = (date?: Date | string | number): string => {
  const d = date != null ? new Date(date) : new Date()
  return d.toISOString()
}

/**
 * Returns today's date in UTC as YYYY-MM-DD for date-only API fields (DB stores UTC).
 */
export const toUtcDateString = (): string => {
  return moment.utc().format('YYYY-MM-DD')
}

/**
 * Converts a date string displayed in MST (MM/DD/YYYY) to UTC YYYY-MM-DD for API (DB stores UTC).
 * @deprecated Prefer mstDateStringToUtcIsoString so the backend stores the correct instant (midnight MST in UTC) and the same day displays correctly.
 */
export const mstDateStringToUtcDateString = (mstMMDDYYYY: string): string => {
  if (!mstMMDDYYYY || mstMMDDYYYY === '') return ''
  const m = moment.tz(mstMMDDYYYY, 'MM/DD/YYYY', UI_DISPLAY_TIMEZONE)
  return m.isValid() ? m.utc().format('YYYY-MM-DD') : mstMMDDYYYY
}

/**
 * Converts a calendar date in MST (MM/DD/YYYY) to the UTC ISO string for "midnight that day in MST".
 * Use this when sending date-only form values to the API so the stored UTC instant displays as the same day in MST.
 * Example: "02/24/2026" (MST) → "2026-02-24T07:00:00.000Z" (winter) so when displayed in MST it shows 02/24/2026.
 */
export const mstDateStringToUtcIsoString = (mstMMDDYYYY: string): string => {
  if (!mstMMDDYYYY || mstMMDDYYYY === '') return ''
  const m = moment.tz(mstMMDDYYYY, 'MM/DD/YYYY', UI_DISPLAY_TIMEZONE)
  return m.isValid() ? m.utc().toISOString() : ''
}

/**
 * Converts a Date from a date picker (user-selected calendar day) to UTC ISO for "midnight that day in MST".
 * Use when sending a Date from the picker to the API so the stored value displays as the same day.
 */
export const pickerDateToUtcIsoStringForMst = (date: Date): string => {
  const year = date.getFullYear()
  const month = date.getMonth()
  const day = date.getDate()
  const m = moment.tz({ year, month, date: day }, UI_DISPLAY_TIMEZONE)
  return m.isValid() ? m.utc().toISOString() : ''
}

/**
 * Converts a date-only string (YYYY-MM-DD) to UTC ISO for "midnight that day in MST".
 * Use when sending a date-only form value to the API (Effective, Due, Expiry, Invoice dates).
 */
export const dateOnlyStringToUtcIsoString = (yyyyMmDd: string): string => {
  if (!yyyyMmDd || typeof yyyyMmDd !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(yyyyMmDd.trim())) return ''
  const m = moment.tz(yyyyMmDd.trim(), 'YYYY-MM-DD', UI_DISPLAY_TIMEZONE)
  return m.isValid() ? m.utc().toISOString() : ''
}

export const Months: Record<number, string> = {
  1: 'January',
  2: 'February',
  3: 'March',
  4: 'April',
  5: 'May',
  6: 'June',
  7: 'July',
  8: 'August',
  9: 'September',
  10: 'October',
  11: 'November',
  12: 'December',
};

export {
  writeXlsxFile,
  readXlsxFile,
  convertDateFromXlsx,
  convertDateToXlsx,
  formatOtrImportDate,
  isOtrImportDateCellBlank,
  isOtrImportDateCellInvalid,
  collectInvalidOtrImportDateFields,
  formatInvalidOtrImportDateMessage,
  hasOtrImportExcelRowData,
  compactString,
  isNanOrZero,
  isEmptyOrUndefined,
  isInvoiceNumber,
  toCurrency,
  getErrorMessage,
  isUserRoleInclude,
  isStaffUser,
  safeTrim,
  safeMoment,
  formatUtcToLocal,
  isMiscUser
}